/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Stores/InputValidationStore" />
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { StoreBase } from "DistributedTask/Flux/Store";
import { InputValidationActions } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/InputValidationActions";
import { EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { InputDetails, InputMetadata } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";

export interface InputValidationStoreState {
    inputsDictionary: { [key: string]: InputDetails };
    errorDictionary: { [key: string]: string };
    isValid: boolean;
    isDirty: boolean;
}

export class InputValidationStore extends StoreBase {

    public static readonly DefaultRequiredErrorMessage = "DefaultRequiredError";

    constructor() {
        super("");
        this._state = this.getState();
        this._defaultRequiredErrorMessage = InputValidationStore.DefaultRequiredErrorMessage;
        this._inputsDirtyDictionary = {};
        this._inputsErrorCount = 0;
        this._inputsDirtyCount = 0;
    }

    public initialize(instanceId: string): void {
        this._actions = ActionsHubManager.GetActionsHub<InputValidationActions>(InputValidationActions, instanceId);
        this._actions.addInputs.addListener(this._addInputs);
        this._actions.removeInputs.addListener(this._removeInputs);
        this._actions.validateInputState.addListener(this._validateInputState);
        this._actions.setInputError.addListener(this._setInputError);
    }

    public disposeInternal(): void {
        this._actions.addInputs.removeListener(this._addInputs);
        this._actions.removeInputs.removeListener(this._removeInputs);
        this._actions.validateInputState.removeListener(this._validateInputState);
        this._actions.setInputError.removeListener(this._setInputError);
    }

    public static getKey(): string {
        return "InputValidationStoreKey";
    }

    public getState(): InputValidationStoreState {
        if (!this._state) {
            this._state = {
                inputsDictionary: {},
                errorDictionary: {},
                isValid: true,
                isDirty: false
            };
        }

        return this._state;
    }

    private _addInputs = (inputDetails: InputDetails[]) => {
        inputDetails.forEach((input) => {
            if (!!input.inputId) {
                this._state.inputsDictionary[input.inputId] = input;

                const isValid = this._isInputValid({
                    inputId: input.inputId,
                    value: input.initialValue,
                    errorMessage: "",
                    isUpdate: input.isUpdate
                });

                if (!isValid) {
                    this._state.errorDictionary[input.inputId] = (!input.initialValue) ? this._defaultRequiredErrorMessage : EndpointsEditorPanelUtils.getInvalidInputErrorMessage(input.inputValidation);
                    this._inputsErrorCount++;
                }
            }
        });
        this._updateState();
    }

    private _removeInputs = (inputIds: string[]) => {
        inputIds.forEach((inputId) => {
            if (!!this._state.errorDictionary[inputId]) {
                this._inputsErrorCount--;
            }
            this._state.errorDictionary[inputId] = undefined;
            this._state.inputsDictionary[inputId] = undefined;
        });
        this._updateState();
    }

    private _validateInputState = (inputMetadata: InputMetadata) => {
        if (!inputMetadata.inputId) {
            return;
        }

        const isInputValid = this._isInputValid(inputMetadata);

        if (!this._state.errorDictionary[inputMetadata.inputId] !== isInputValid) {
            isInputValid ? this._inputsErrorCount-- : this._inputsErrorCount++;
        }
        let errorMessage = isInputValid ? "" : EndpointsEditorPanelUtils.getInvalidInputErrorMessage(this._state.inputsDictionary[inputMetadata.inputId].inputValidation);
        this._state.errorDictionary[inputMetadata.inputId] = errorMessage;
        this._updateState();
    }

    private _isInputValid(inputMetadata: InputMetadata): boolean {
        const isDirty = this._checkAndSetDirty(inputMetadata.inputId, inputMetadata.value);
        const isConfidential = this._state.inputsDictionary[inputMetadata.inputId].isConfidential;
        if (inputMetadata.isUpdate && isConfidential && !isDirty) {
            return true;
        }

        return EndpointsEditorPanelUtils.isInputValid(inputMetadata.value, this._state.inputsDictionary[inputMetadata.inputId].inputValidation);
    }

    private _setInputError = (inputMetadata: InputMetadata) => {
        if (inputMetadata.inputId && inputMetadata.errorMessage) {
            if (!this._state.errorDictionary[inputMetadata.inputId]) {
                this._inputsErrorCount++;
            }

            this._state.errorDictionary[inputMetadata.inputId] = inputMetadata.errorMessage;
            this._updateState();
        }
    }

    private _checkAndSetDirty(inputId: string, value: any): boolean {
        const oldValue = this._state.inputsDictionary[inputId].initialValue;
        const isDirty = value !== oldValue;
        if (!!this._inputsDirtyDictionary[inputId] !== isDirty) {
            this._inputsDirtyDictionary[inputId] = isDirty;
            isDirty ? this._inputsDirtyCount++ : this._inputsDirtyCount--;
        }

        return isDirty;
    }

    private _updateState() {
        this._state.isValid = this._inputsErrorCount === 0 ? true : false;
        this._state.isDirty = this._inputsDirtyCount > 0 ? true : false;
        this.emitChanged();
    }

    private _state: InputValidationStoreState;
    private _actions: InputValidationActions;
    private _defaultRequiredErrorMessage: string;
    private _inputsDirtyDictionary: { [key: string]: boolean };
    private _inputsErrorCount: number;
    private _inputsDirtyCount: number;
}